import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, BarChart3, Users, Inbox, MessageSquare, Activity } from 'lucide-react';
import DashboardOverview from './DashboardOverview';
import DashboardActivity from './DashboardActivity';
import DashboardCampaigns from './DashboardCampaigns';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDashboardTab = () => {
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [activityItems, setActivityItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, campaignsRes, messagesRes, submissionsRes, ccRes] = await Promise.all([
        fetch(`${API}/api/analytics/dashboard`),
        fetch(`${API}/api/analytics/campaigns`),
        fetch(`${API}/api/contact`),
        fetch(`${API}/api/submissions`),
        fetch(`${API}/api/contact/cineconnect-interest`),
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (campaignsRes.ok) setCampaigns(await campaignsRes.json());

      const allItems = [];
      if (messagesRes.ok) {
        const messages = await messagesRes.json();
        messages.forEach(m => allItems.push({ ...m, _type: 'message' }));
      }
      if (submissionsRes.ok) {
        const submissionsData = await submissionsRes.json();
        const submissions = submissionsData.submissions || submissionsData;
        (Array.isArray(submissions) ? submissions : []).forEach(s => allItems.push({ ...s, _type: 'submission' }));
      }
      if (ccRes.ok) {
        const ccItems = await ccRes.json();
        ccItems.forEach(c => allItems.push({ ...c, _type: 'joinpack' }));
      }

      allItems.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setActivityItems(allItems);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diffMs = new Date() - new Date(dateStr);
    const mins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMs / 3600000);
    const days = Math.floor(diffMs / 86400000);
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return formatDate(dateStr);
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'subscriber': return <Users size={16} className="text-green-400" />;
      case 'submission': return <Inbox size={16} className="text-blue-400" />;
      case 'message': return <MessageSquare size={16} className="text-purple-400" />;
      default: return <Activity size={16} className="text-swp-white-ghost" />;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <RefreshCw className="w-8 h-8 text-swp-ice animate-spin mx-auto mb-4" />
        <p className="text-swp-white-ghost/70">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard-tab">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <BarChart3 className="text-swp-ice" /> Dashboard
          </h2>
          <p className="text-swp-white-ghost/70 text-sm mt-1">Overview of site performance and activity</p>
        </div>
        <button onClick={fetchData} className="p-2 text-swp-white-ghost hover:text-swp-white transition-colors" title="Refresh" data-testid="refresh-dashboard">
          <RefreshCw size={20} />
        </button>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 mb-6 border-b border-swp-border pb-4">
        {['overview', 'activity', 'campaigns'].map((section) => (
          <button key={section} onClick={() => setActiveSection(section)} data-testid={`section-${section}`}
            className={`px-4 py-2 rounded-full font-mono text-xs uppercase tracking-widest transition-colors ${
              activeSection === section ? 'bg-swp-ice text-white' : 'text-swp-white-ghost hover:text-swp-white hover:bg-white/5'
            }`}>
            {section === 'overview' ? 'Overview' : section === 'activity' ? 'Activity Feed' : 'Email Campaigns'}
          </button>
        ))}
      </div>

      {/* Sections */}
      {activeSection === 'overview' && (
        <DashboardOverview
          stats={stats}
          activityItems={activityItems}
          formatTimeAgo={formatTimeAgo}
          onViewAllActivity={() => setActiveSection('activity')}
          getActivityIcon={getActivityIcon}
        />
      )}
      {activeSection === 'activity' && (
        <DashboardActivity
          activityItems={activityItems}
          formatDate={formatDate}
          formatTimeAgo={formatTimeAgo}
          onRefresh={fetchData}
        />
      )}
      {activeSection === 'campaigns' && (
        <DashboardCampaigns campaigns={campaigns} formatDate={formatDate} />
      )}
    </div>
  );
};

export default AdminDashboardTab;
